import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ClxFormComponent, FormEditModes } from '@forms/clx-forms/clx.form.component';
import { __i18n } from '@localisation';
import { RoutingInfoService } from '@routing';
import { MessagesService, MessageType } from '@ui-messages';
import { custom } from 'devextreme/ui/dialog';
import { ThirdPartyApp, ThirdPartyAppsService } from './third-party-apps.service';
import { LoaderService } from '@loader';
import { DataChangesHandlerService } from '@check';


@Component({
    selector: 'third-party-app',
    templateUrl: './third-party-app.template.html',
    standalone: false
})
export class ThirdPartyAppComponent {

    constructor(
        private routingInfo: RoutingInfoService,
        private service: ThirdPartyAppsService,
        private router: Router,
        private messages: MessagesService,
        private loader: LoaderService,
        private dataChangesService: DataChangesHandlerService
    ){}

    public async ngAfterViewInit(){
        const routingParams = this.routingInfo.getCurrentRoutingParams();
        this.thirdPartyId = routingParams.thirdpartyid;
        this.mode =  this.thirdPartyId ? FormEditModes.update : FormEditModes.create;
        this.refresh();
    }

    @ViewChild('form')
    public form: ClxFormComponent

    public mode: FormEditModes = FormEditModes.create;

    public thirdPartyId: string;

    public data: any;
    public isPrimaryTenant: boolean = true;
    public isUpdatingPrimarySecret: boolean = false;
    public isUpdatingSecondarySecret: boolean = false;

    private handleSaveError(e: any) {
        if (typeof e.error?.error?.errorCode === 'number') {
            switch (e.error.error.errorCode) {
                case -1:
                    throw new Error(
                        __i18n(
                            'An app with the same name already exists. Please specify a unique name for the app.',
                            'third-party-app.message.nameuniqueerror'
                        )
                    );
            }
        }
        throw new Error(
            __i18n('An unexpected error has occured. Please try again.', 'third-party-app.message.unexpectederror')
        );
    }

    public async create(thirdPartyProduct: ThirdPartyApp) {
        const app = thirdPartyProduct as any;
        thirdPartyProduct.redirectUris = app.redirectUris.map((current) => current.uri).filter((current) => !!current);
        
        try {
            const result = await this.service.createThirdPartyApp(thirdPartyProduct);
            this.router.navigateByUrl('/Administration/ThirdPartyApps/' + result.id);
            this.messages.addMessage({
                type: MessageType.success,
                text: __i18n('Your app was successfully registered.', 'third-party-app.message.success')
            });
        } catch (e) {
            this.handleSaveError(e);
        }
    }

    public async update(thirdPartyProduct: ThirdPartyApp) {
        const app = thirdPartyProduct as any;
        if(thirdPartyProduct.redirectUris){
            thirdPartyProduct.redirectUris = app.redirectUris.map((current) => current.uri).filter((current) => !!current);
        }

        try {
            await this.service.updateThirdPartyApp(this.thirdPartyId, thirdPartyProduct);
            this.messages.addMessage({
                type: MessageType.success,
                text: __i18n('Your changes have been saved.', 'third-party-app.message.saved')
            });
            await this.refresh();
            setTimeout(() => { this.dataChangesService.markAsClean(); }, 100);
        } catch (e) {
            this.handleSaveError(e);
        }
    }

    public confirmDelete() : Promise<boolean> {
        return new Promise(async(resolve, reject) => {
            const dialog = custom({
                title: this.isPrimaryTenant
                    ? __i18n('Confirm delete', 'third-party-app.delete.confirm.title')
                    : __i18n('Confirm unregister', 'third-party-app.unregister.confirm.title'),
                message: this.isPrimaryTenant
                    ? __i18n('Are you sure, you want to delete this third-party app?', 'third-party-app.delete.confirm.text')
                    : __i18n('Are you sure, you want to unregister this third-party app?', 'third-party-app.unregister.confirm.text'),
                buttons: [
                    {
                        text: __i18n('Yes', 'third-party-app.delete.confirm.button.yes'),
                        onClick: () => {
                            dialog.hide();
                            resolve(true);
                        }
                    },
                    {
                        text: __i18n('No', 'third-party-app.delete.confirm.button.no'),
                        accessKey: 'esc',
                        onClick: () => {
                            dialog.hide();
                            resolve(false);
                        }
                    }
                ]
            });

            try {
                await dialog.show();
            }catch(e) {
                dialog.hide();
                resolve(false);
            }
        });
    }

    public async delete() {
        if (await this.confirmDelete()) {
            try {
                await this.service.deleteThirdPartyApp(this.thirdPartyId);
                this.router.navigateByUrl('/Administration/ThirdPartyApps');
                this.messages.addMessage({
                    text: this.isPrimaryTenant
                        ? __i18n('The app was deleted.', 'third-party-app.delete.success')
                        : __i18n('The app was unregistered.', 'third-party-app.unregister.success'),
                    type: MessageType.success
                });
            } catch (e) {
                this.messages.addMessage({
                    text: this.isPrimaryTenant
                        ? __i18n('An error occurred while deleting the app.', 'third-party-app.delete.error')
                        : __i18n('Ann error occurred while unregistering the app.', 'third-party-app.unregister.error'),
                    type: MessageType.error
                });
            }
        }
    }

    public async refresh(){
        if(this.mode === FormEditModes.update){
            const loaderId = this.loader.start();
            try {
                const data = await this.service.getThirdPartyApp(this.thirdPartyId);
                data.postLogoutRedirectUris = data.postLogoutRedirectUris.map((current) => {
                    return { uri : current}
                });
                data.redirectUris = data.redirectUris.map((current) => {
                    return { uri : current}
                });
                if (data.secrets) {
                    data.primarySecret = data.secrets.find((current) => current.isPrimary)?.value || ''
                    data.secondarySecret = data.secrets.find((current) => !current.isPrimary)?.value || ''
                } else {
                    data.primarySecret = data.secondarySecret = ''
                }
                this.data = data;
                this.isPrimaryTenant = !!data.isPrimaryTenant;
                setTimeout(() => { this.dataChangesService.markAsClean(); }, 12);
            } catch (e) {
                console.error(e);
                this.form.resetData();
                this.router.navigateByUrl('/Administration/ThirdPartyApps');
                this.messages.addMessage({
                    text: __i18n('Could not find the app.', 'third-party-app.load.not-found'),
                    type: MessageType.error
                });
            } finally {
                this.loader.stop(loaderId);
            }
        }
    }

    public async refreshSecrets(){
        if(this.mode === FormEditModes.update){
            const data = await this.service.getThirdPartyApp(this.thirdPartyId);
            this.form.setData({
                secrets : data.secrets
            });
        }
    }

    public async updatePrimarySecret(){
        this.isUpdatingPrimarySecret = true;
        await this.service.updatePrimarySecret(this.thirdPartyId);
        this.isUpdatingPrimarySecret = false;
        this.refresh()
    }

    public async updateSecondarySecret(){
        this.isUpdatingSecondarySecret = true;
        await this.service.updateSecondarySecret(this.thirdPartyId);
        this.isUpdatingSecondarySecret = false;
        this.refresh()
    }

    public preventEnter(e){
        if(e.keyCode === 13){
            e.preventDefault();
        }
    }

}